import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ChevronLeft } from "lucide-react";

export default function AdminReviewQueuePage() {
  const api = useApi();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purgeDays, setPurgeDays] = useState(30);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api("/admin/review-queue");
      if (r.ok) { const d = await r.json(); setItems(d.items || d.users || []); }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const approve = async (uid) => {
    const r = await api(`/admin/review-queue/${uid}/approve`, { method: "POST" });
    if (r.ok) { toast.success("Approved"); load(); }
    else { const d = await r.json().catch(() => ({})); toast.error(d.detail || "Approve failed"); }
  };
  const reject = async (uid) => {
    if (!window.confirm("Reject this signup?")) return;
    const r = await api(`/admin/review-queue/${uid}/reject`, { method: "POST" });
    if (r.ok) { toast.success("Rejected"); load(); }
    else { const d = await r.json().catch(() => ({})); toast.error(d.detail || "Reject failed"); }
  };
  const purge = async () => {
    if (!window.confirm(`Purge stale review-queue rows older than ${purgeDays} days?`)) return;
    const r = await api("/admin/review-queue/purge-stale", { method: "POST", body: JSON.stringify({ days: purgeDays }) });
    if (r.ok) { toast.success("Purged"); load(); }
    else { const d = await r.json().catch(() => ({})); toast.error(d.detail || "Purge failed"); }
  };

  return (
    <div>
      <button onClick={() => nav("/admin")} className="text-sm text-primary inline-flex items-center gap-1 mb-4"><ChevronLeft size={16}/> Back</button>
      <PageHeader
        title="Review queue"
        subtitle="Approve or reject registrations that didn't auto-match the directory."
        actions={
          <div className="flex items-center gap-2">
            <Input type="number" min={7} max={365} value={purgeDays} onChange={(e) => setPurgeDays(parseInt(e.target.value || "30", 10))} className="w-20 text-center" />
            <Button variant="outline" onClick={purge}>Purge stale</Button>
          </div>
        }
      />
      <Card className="p-5">
        {loading ? <div className="text-sm text-muted-foreground">Loading…</div> : items.length === 0 ? (
          <div className="text-sm text-muted-foreground italic">No pending reviews.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-muted-foreground border-b border-border">
                <th className="py-2 pr-3">Name</th><th className="py-2 pr-3">Email</th><th className="py-2 pr-3">GL Ref</th><th className="py-2 pr-3">Submitted</th><th className="py-2 pr-3"></th></tr></thead>
              <tbody>
                {items.map((u) => (
                  <tr key={u.user_id} className="border-b border-border/60">
                    <td className="py-2 pr-3">{u.name}</td>
                    <td className="py-2 pr-3">{u.email}</td>
                    <td className="py-2 pr-3 font-mono text-xs">{u.gl_ref || "—"}</td>
                    <td className="py-2 pr-3 text-xs text-muted-foreground">{new Date(u.created_at).toLocaleString("en-GB")}</td>
                    <td className="py-2 pr-3 flex gap-2 justify-end">
                      <Button size="sm" onClick={() => approve(u.user_id)} data-testid={`rq-approve-${u.user_id}`}>Approve</Button>
                      <Button size="sm" variant="outline" onClick={() => reject(u.user_id)} data-testid={`rq-reject-${u.user_id}`}>Reject</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
